import { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import CategoryIcon from '@material-ui/icons/CategoryOutlined';
import {
  CompoundEntityRef,
  Entity,
  parseEntityRef,
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_OWNED_BY,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  CatalogApi,
  catalogApiRef,
  entityRouteParams,
  entityRouteRef,
  getEntityRelations,
  useEntity,
  useEntityPresentation,
} from '@backstage/plugin-catalog-react';
import { readableName } from '../../shared/EntityDisplayLink';
import { entityTitle } from '../../shared/entityCells';
import { ByxMessageKey, useByxT } from '../../shared/translations';
import { byxKindChips, byxScheme } from '../../theme/tokens';
import { ByxCard, CardMessage, ErrorCard, LoadingCard } from '../ByxCard';
import { ByxCardBoundary } from '../ByxCardBoundary';
import { sharedLoad } from '../cache';
import { RELATED_ENTITY_FIELDS, useRowNavigation } from '../RelationPanels';

/**
 * Fornecida por / Consumida por of an API (AC-6): the components of its
 * `apiProvidedBy` and `apiConsumedBy` relations. Both cards read them
 * through one batched call, shared between the two cards.
 */

type ComponentsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; byRef: Map<string, Entity> };

function relationRefs(entity: Entity, relation: string): string[] {
  return getEntityRelations(entity, relation).map(ref =>
    stringifyEntityRef(ref),
  );
}

/** True when some component declares that it provides the API. */
export function hasApiProviders(entity: Entity): boolean {
  return relationRefs(entity, RELATION_API_PROVIDED_BY).length > 0;
}

/** The refs of both cards, sorted: the key of the shared load. */
export function apiComponentRefs(entity: Entity): string[] {
  return [
    ...new Set([
      ...relationRefs(entity, RELATION_API_PROVIDED_BY),
      ...relationRefs(entity, RELATION_API_CONSUMED_BY),
    ]),
  ].sort((a, b) => a.localeCompare(b));
}

export async function loadApiComponents(
  catalogApi: CatalogApi,
  refs: string[],
): Promise<Map<string, Entity>> {
  const byRef = new Map<string, Entity>();
  if (refs.length === 0) {
    return byRef;
  }
  const { items } = await catalogApi.getEntitiesByRefs({
    entityRefs: refs,
    fields: RELATED_ENTITY_FIELDS,
  });
  items.forEach((item, index) => {
    if (item) {
      byRef.set(refs[index], item);
    }
  });
  return byRef;
}

function useApiComponents(entity: Entity): ComponentsState {
  const catalogApi = useApi(catalogApiRef);
  const refs = useMemo(() => apiComponentRefs(entity), [entity]);
  const key = refs.join(',');
  const [state, setState] = useState<ComponentsState>({ status: 'loading' });
  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    sharedLoad(`api-components|${key}`, () =>
      loadApiComponents(catalogApi, refs),
    ).then(
      byRef => active && setState({ status: 'ready', byRef }),
      () => active && setState({ status: 'error' }),
    );
    return () => {
      active = false;
    };
    // `refs` is derived from `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi, key]);
  return state;
}

const useStyles = makeStyles(theme => {
  const mode = theme.palette.type === 'dark' ? 'dark' : 'light';
  const scheme = byxScheme(mode);
  return {
    list: {
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      minHeight: 40,
      margin: theme.spacing(0, -1),
      padding: theme.spacing(0, 1),
      borderRadius: 10,
      cursor: 'pointer',
      '& + &': { borderTop: `1px solid ${scheme.line}` },
      '&:hover': { backgroundColor: scheme.hover },
    },
    icon: {
      flex: 'none',
      display: 'inline-flex',
      color: byxKindChips[mode].component.ink,
      '& svg': { fontSize: 16 },
    },
    name: {
      flex: '1 1 auto',
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontSize: 14,
      fontWeight: 600,
      '& a': { color: scheme.accent },
    },
    owner: {
      flex: '0 1 auto',
      maxWidth: 120,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontSize: 12,
      lineHeight: '17px',
      color: scheme.mutedText,
    },
  };
});

/** The owner's display name as text (MC-2: never a raw ref). */
function OwnerName({ entityRef }: Readonly<{ entityRef: CompoundEntityRef }>) {
  const classes = useStyles();
  const { primaryTitle } = useEntityPresentation(entityRef, {
    defaultKind: 'group',
  });
  const name = readableName(primaryTitle, entityRef);
  return (
    <span className={classes.owner} title={name}>
      {name}
    </span>
  );
}

function ComponentRow(props: Readonly<{ entityRef: string; entity?: Entity }>) {
  const { entityRef, entity } = props;
  const classes = useStyles();
  const entityRoute = useRouteRef(entityRouteRef);
  const compound = parseEntityRef(entityRef);
  const { Icon } = useEntityPresentation(compound);
  const RowIcon = Icon || CategoryIcon;
  const title = entity ? entityTitle(entity) : compound.name;
  const [owner] = entity ? getEntityRelations(entity, RELATION_OWNED_BY) : [];
  return (
    <li className={classes.row}>
      <span className={classes.icon} aria-hidden="true">
        <RowIcon />
      </span>
      <span className={classes.name} title={title}>
        <Link to={entityRoute(entityRouteParams(compound))}>{title}</Link>
      </span>
      {owner && <OwnerName entityRef={owner} />}
    </li>
  );
}

function ComponentsBody(
  props: Readonly<{ relation: string; titleKey: ByxMessageKey }>,
) {
  const { relation, titleKey } = props;
  const { entity } = useEntity();
  const { t } = useByxT();
  const classes = useStyles();
  const handleClick = useRowNavigation('li');
  const state = useApiComponents(entity);
  const title = t(titleKey);
  const refs = relationRefs(entity, relation);

  if (state.status === 'loading') {
    return <LoadingCard title={title} />;
  }
  if (state.status === 'error') {
    return <ErrorCard title={title} />;
  }
  if (refs.length === 0) {
    // Only Consumida por gets here: Fornecida por is hidden when empty.
    return (
      <ByxCard title={title}>
        <CardMessage tone="muted">{t('entity.api.noConsumers')}</CardMessage>
      </ByxCard>
    );
  }
  return (
    <ByxCard title={title} count={refs.length}>
      {/* The list is only a delegation surface; the keyboard target stays
          the real link inside the row (SR-13). */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <ul className={classes.list} onClick={handleClick}>
        {refs.map(ref => (
          <ComponentRow
            key={ref}
            entityRef={ref}
            entity={state.byRef.get(ref)}
          />
        ))}
      </ul>
    </ByxCard>
  );
}

/** Fornecida por: the components that provide the API (hidden if none). */
export function ApiProvidedByCard() {
  const { t } = useByxT();
  return (
    <ByxCardBoundary title={t('entity.api.providedBy')}>
      <ComponentsBody
        relation={RELATION_API_PROVIDED_BY}
        titleKey="entity.api.providedBy"
      />
    </ByxCardBoundary>
  );
}

/**
 * Consumida por: the components that consume the API. With none, the card
 * stays and says so, with no link to generic docs.
 */
export function ApiConsumedByCard() {
  const { t } = useByxT();
  return (
    <ByxCardBoundary title={t('entity.api.consumedBy')}>
      <ComponentsBody
        relation={RELATION_API_CONSUMED_BY}
        titleKey="entity.api.consumedBy"
      />
    </ByxCardBoundary>
  );
}
